// URL of the backend's fallback image, still returned by the API for users without a custom avatar.
// TODO: remove after backend changes are deployed
const OLD_DEFAULT_AVATAR_PATTERN = /learnbeat-default-avatar/i

// Palette for the avatar gradient
// Colors are ordered around the hue wheel, so we can pair only nearby colors. That avoids a gray
// middle part in the gradient. See https://www.learnui.design/tools/gradient-generator.html
// WARNING: if you add or remove a color, the default avatars of our users will change
const AVATAR_COLORS = [
    '#e63531',
    '#ee992e',
    '#ad9300',
    '#9eb72e',
    '#74a22f',
    '#43842a',
    '#1a9698',
    '#3684bd',
    '#5a5a8b',
    '#704484',
    '#a0288c',
    '#e34684',
]

export default class AvatarUtil {

    /**
     * Whether the given avatar url should be treated as "no avatar", either because it's
     * empty or because it's the backend's fallback image
     *
     * @param {string} avatarUrl
     * @returns {boolean}
     */
    static hasNoAvatar(avatarUrl) {
        return !avatarUrl || OLD_DEFAULT_AVATAR_PATTERN.test(avatarUrl)
    }

    /**
     * Generate a hash from the name of the user, so the avatar is constant. Based on djb2
     * @param {string} string Name of the user
    */
    static hashString(string = '') {
        let hash = 0
        for (let i = 0; i < string.length; i++) {
            hash = (hash * 31 + string.charCodeAt(i)) % 2147483647
        }
        return hash
    }

    /**
     * If the given model has no avatar, generate one and set the default avatar
     * @param {Backbone.Model} model StudentModel, TeacherModel or UserModel instance
    */
    static generateAvatarIfMissing(model) {
        if (AvatarUtil.hasNoAvatar(model.get('avatar'))) {
            model.set('avatar', AvatarUtil.getAvatarUrl(model))
        }
    }

    /**
     * Create a circle avatar with initial as a data: URI
     * @param {Backbone.Model} model StudentModel, TeacherModel or UserModel instance
     * @returns {string} data:image/svg+xml URI
     */
    static getAvatarUrl(model) {

        // For teachers use the last name, for students the first name
        const isTeacher = model.constructor.type === 'teacher' || !!model.get('is_teacher')
        const name = isTeacher ? model.get('sortable_last_name') : model.first_name_last_name()
        const initial = (name || '')[0]?.toUpperCase() ?? ''

        // Create the hash and make it possible to get 3 independant values from it (start, step and direction)
        let remainder = AvatarUtil.hashString(name)
        const take = (base) => {
            const value = remainder % base
            remainder = Math.floor(remainder / base)
            return value
        }

        // Get a start color for the gradient
        const fromIndex = take(AVATAR_COLORS.length)
        // A step of 2 or 3 to the end color
        const step = 2 + take(2)
        // Direction forward or backward
        const direction = take(2) === 0 ? 1 : -1

        const from = AVATAR_COLORS[fromIndex]
        const to = AVATAR_COLORS[(fromIndex + direction * step + AVATAR_COLORS.length) % AVATAR_COLORS.length]

        // Create svg with a circle, gradient and initial letter
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><circle cx="32" cy="32" r="32" fill="url(#g)"/><text x="32" y="32" text-anchor="middle" dominant-baseline="central" font-family="system-ui, sans-serif" font-size="34" font-weight="600" fill="#fff">${initial}</text></svg>`

        // Create a svg data url and escape ' to make it safe
        return `data:image/svg+xml,${encodeURIComponent(svg).replace(/'/g, '%27')}`
    }
}
